import { ScrollArea } from "../ScrollArea/index.js";
import { Portal } from "../Portal/index.js";
import Modal_module_default from "./Modal.module.js";
import React from "react";
import { IconClose, IconRectangleCollapse } from "@synthesis/shared-icons";
import { cn } from "@synthesis/shared-utils";
import { jsx, jsxs } from "react/jsx-runtime";
import { Dialog } from "@base-ui/react/dialog";
import { Rnd } from "react-rnd";
//#region src/Modal/index.tsx
const minMarginY = 0;
const minMarginX = 100;
const calculateInitialState = ({ minWidth, maxWidth, minHeight, maxHeight }) => {
	const windowWidth = window.innerWidth;
	const windowHeight = window.innerHeight;
	const calculatedWidth = Math.max(minWidth, Math.min(maxWidth, windowWidth * .55));
	const calculatedHeight = Math.max(minHeight, Math.min(maxHeight, windowHeight - 240 - 60));
	const y = windowHeight - calculatedHeight - 60 < 240 ? windowHeight - calculatedHeight - 60 : 240;
	return {
		width: calculatedWidth,
		height: calculatedHeight,
		x: (windowWidth - calculatedWidth) / 2,
		y
	};
};
const DraggableContent = ({ className, children, title, closeModal, customButton }) => {
	const minHeight = 600;
	const minWidth = 500;
	const maxHeight = 744;
	const maxWidth = 1400;
	const [max, setMax] = React.useState(false);
	const lastState = React.useRef(null);
	const [state, setState] = React.useState(() => calculateInitialState({
		minWidth,
		maxWidth,
		minHeight,
		maxHeight
	}));
	return /* @__PURE__ */ jsx(Rnd, {
		minHeight,
		maxWidth: max ? "100%" : maxWidth,
		minWidth,
		maxHeight: max ? "100%" : maxHeight,
		size: {
			width: max ? window.innerWidth : state.width,
			height: max ? "100%" : state.height
		},
		position: {
			...state,
			x: max ? 0 : Math.min(window.innerWidth - minMarginX, Math.max(-state.width + minMarginX, state.x)),
			y: max ? 0 : Math.min(window.innerHeight + window.scrollY, Math.max(minMarginY, state.y + window.scrollY))
		},
		onDragStart: (e, d) => {
			if (max && lastState.current) {
				setMax(false);
				setState({
					...state,
					x: e.pageX - lastState.current.width / 2,
					y: d.y - window.scrollY,
					width: lastState.current.width,
					height: lastState.current.height
				});
			}
		},
		onDragStop: (_e, d) => {
			setState({
				...state,
				x: d.x,
				y: d.y - window.scrollY
			});
		},
		onResizeStop: (_e, _direction, ref, _delta, position) => {
			setState({
				...position,
				width: Number.parseInt(ref.style.width, 10),
				height: Number.parseInt(ref.style.height, 10),
				y: position.y - window.scrollY
			});
		},
		dragHandleClassName: "window-bar",
		className: "bg-neutrals-99 fixed z-50 h-screen w-full overflow-hidden rounded-lg shadow-lg",
		children: /* @__PURE__ */ jsxs("div", {
			className: cn(className, "h-screen"),
			children: [
				/* @__PURE__ */ jsx(DraggableModalTitle, { children: title }),
				/* @__PURE__ */ jsx(ScrollArea, {
					rootClassName: "overflow-y-scroll",
					style: {
						maxHeight: max ? "100%" : state.height - 44,
						height: "100%"
					},
					children
				}),
				customButton?.buttonType === "isFullScreen" ? /* @__PURE__ */ jsx("div", {
					onClick: () => setMax(!max),
					children: customButton.button
				}) : null,
				/* @__PURE__ */ jsx("button", {
					"data-cy": "modal-button-iconClose",
					onClick: closeModal,
					type: "button",
					"aria-label": "close",
					className: "text-neutrals-50 absolute right-0 top-0 flex justify-end p-3 outline-none",
					children: /* @__PURE__ */ jsx(IconClose, { className: "h-5 w-5 transition-colors" })
				})
			]
		})
	});
};
const ModalContext = React.createContext(null);
const StyledModalContent = ({ children, className, closable = true, style, handleUnfold, ...rest }) => {
	const context = React.useContext(ModalContext);
	return /* @__PURE__ */ jsxs(Dialog.Portal, { children: [/* @__PURE__ */ jsx(Dialog.Backdrop, {
		className: Modal_module_default.overlay,
		onClick: () => context?.closeModal()
	}), /* @__PURE__ */ jsx(Dialog.Viewport, { children: /* @__PURE__ */ jsxs(Dialog.Popup, {
		className: cn(Modal_module_default.content, className),
		style,
		...rest,
		children: [
			children,
			handleUnfold ? /* @__PURE__ */ jsx("button", {
				onClick: handleUnfold,
				type: "button",
				className: "text-neutrals-50 absolute right-8 top-0 flex justify-end p-3 outline-none",
				children: /* @__PURE__ */ jsx(IconRectangleCollapse, { className: "h-5 w-4 transition-colors" })
			}) : null,
			closable ? /* @__PURE__ */ jsx(Modal.Close, { children: /* @__PURE__ */ jsx("button", {
				"data-cy": "modal-button-iconClose",
				type: "button",
				"aria-label": "close",
				className: "text-neutrals-50 absolute right-0 top-0 flex justify-end p-3 outline-none",
				children: /* @__PURE__ */ jsx(IconClose, { className: "h-5 w-5 transition-colors" })
			}) }) : null
		]
	}) })] });
};
const DraggableModalTitle = ({ className, children }) => /* @__PURE__ */ jsx("div", {
	className: cn("window-bar", "text-type-minor bg-neutrals-90 flex items-center gap-[14px] rounded-t-[14px] px-6 py-3 text-sm font-medium", className),
	children: /* @__PURE__ */ jsx("span", {
		className: "truncate break-all",
		children
	})
});
const ModalTitle = ({ className, children }) => /* @__PURE__ */ jsx(Dialog.Title, {
	"data-cy": "modalComponent-title",
	className: cn("window-bar", "text-type-minor bg-neutrals-90 flex w-full items-center gap-[14px] rounded-t-[14px] py-3 pl-6 pr-12 text-sm font-medium", className),
	children: /* @__PURE__ */ jsx("span", {
		className: "truncate break-all",
		children
	})
});
const ModalDescription = ({ className, children }) => /* @__PURE__ */ jsx(Dialog.Description, {
	className: cn(Modal_module_default.description, className),
	children
});
const ModalContent = ({ className, disableOnOpenAutoFocus = true, children, style, closable, handleUnfold, ...rest }) => {
	const initialFocus = !disableOnOpenAutoFocus;
	return /* @__PURE__ */ jsx(StyledModalContent, {
		className: cn("min-h-[120px] w-[40vw] min-w-[20vw] max-w-[90vw] rounded-[14px] shadow-lg", className),
		style: typeof style === "function" ? void 0 : style,
		initialFocus,
		closable,
		handleUnfold,
		...rest,
		children
	});
};
const ModalClose = ({ children }) => {
	const context = React.useContext(ModalContext);
	if (context) return React.cloneElement(children, { onClick: (event) => {
		children.props.onClick?.(event);
		context.closeModal();
	} });
	return /* @__PURE__ */ jsx(Dialog.Close, { render: children });
};
const AutoModalRoot = React.memo(({ trigger, onOpenChange, children }) => /* @__PURE__ */ jsxs(Dialog.Root, {
	onOpenChange,
	children: [trigger ? /* @__PURE__ */ jsx(Dialog.Trigger, { render: trigger }) : null, children]
}));
AutoModalRoot.displayName = "AutoModalRoot";
const ManualModalProvider = ({ children }) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const closeModal = () => setIsOpen(false);
	const openModal = () => setIsOpen(true);
	return /* @__PURE__ */ jsx(ModalContext.Provider, {
		value: {
			isOpen,
			setIsOpen,
			openModal,
			closeModal
		},
		children
	});
};
const ModifyManualModalProvider = ({ isOpen, setIsOpen, openModal, closeModal, children }) => {
	if (isOpen !== void 0 && setIsOpen && openModal && closeModal) return /* @__PURE__ */ jsx(ModalContext.Provider, {
		value: {
			isOpen,
			setIsOpen,
			openModal,
			closeModal
		},
		children
	});
	return null;
};
const useManualModalState = () => {
	const context = React.useContext(ModalContext);
	if (!context) throw new Error("useManualModalState must be used within a ManualModalProvider");
	return context;
};
const ManualModalTrigger = ({ children }) => {
	const { openModal } = useManualModalState();
	return React.cloneElement(children, { onClick: openModal });
};
const ManualModalRoot = React.memo(({ trigger, onOpenChange, children }) => {
	const { isOpen, closeModal } = useManualModalState();
	React.useEffect(() => {
		onOpenChange?.(isOpen);
	}, [isOpen, onOpenChange]);
	return /* @__PURE__ */ jsxs(Dialog.Root, {
		open: isOpen,
		onOpenChange: (open) => !open && closeModal(),
		children: [trigger ? /* @__PURE__ */ jsx(ManualModalTrigger, { children: trigger }) : null, isOpen ? children : null]
	});
});
ManualModalRoot.displayName = "ManualModalRoot";
const ControlledModalProvider = ({ isOpen, setIsOpen, children }) => {
	const closeModal = () => setIsOpen(false);
	const openModal = () => setIsOpen(true);
	return /* @__PURE__ */ jsx(ModalContext.Provider, {
		value: {
			isOpen,
			setIsOpen,
			openModal,
			closeModal
		},
		children
	});
};
const ModalRoot = ({ manual, onOpenChange, children, isOpen, setIsOpen, title = "title", type = "default", trigger, className, closeModal: externalCloseModal, openModal, customButton }) => {
	if (type === "draggable") return isOpen ? /* @__PURE__ */ jsx(Portal, { children: /* @__PURE__ */ jsx("div", {
		className: "absolute inset-0",
		children: /* @__PURE__ */ jsx(DraggableContent, {
			className,
			title,
			closeModal: externalCloseModal,
			customButton,
			children
		})
	}) }) : null;
	if (isOpen !== void 0 && setIsOpen) return /* @__PURE__ */ jsx(ControlledModalProvider, {
		isOpen,
		setIsOpen,
		children: /* @__PURE__ */ jsx(ManualModalRoot, {
			onOpenChange,
			trigger,
			children
		})
	});
	if (manual && isOpen !== void 0 && setIsOpen && openModal && externalCloseModal) return /* @__PURE__ */ jsx(ModifyManualModalProvider, {
		isOpen,
		setIsOpen,
		openModal,
		closeModal: externalCloseModal,
		children: /* @__PURE__ */ jsx(ManualModalRoot, {
			onOpenChange,
			trigger,
			children
		})
	});
	if (manual) return /* @__PURE__ */ jsx(ManualModalProvider, { children: /* @__PURE__ */ jsx(ManualModalRoot, {
		onOpenChange,
		trigger,
		children
	}) });
	return /* @__PURE__ */ jsx(AutoModalRoot, {
		onOpenChange,
		trigger,
		children
	});
};
const ModalWrapper = ({ children, trigger }) => {
	if (trigger) return /* @__PURE__ */ jsx(Modal.Root, {
		trigger,
		children
	});
	return null;
};
const Modal = {
	Wrapper: ModalWrapper,
	Root: ModalRoot,
	Content: ModalContent,
	Title: ModalTitle,
	Description: ModalDescription,
	Close: ModalClose
};
//#endregion
export { Modal, ModalWrapper, ModifyManualModalProvider, useManualModalState };

//# sourceMappingURL=index.js.map